import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { useAuth } from '../auth';
import {
  createPortalPhoneBooking,
  getPortalBookingState,
  PortalApiError,
} from '../services/portalApi';
import type { PortalBookingStateResponse } from '../types/api';

const standaloneEventValue = '__standalone__';

export function BookPage() {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<PortalBookingStateResponse | null>(null);
  const [eventId, setEventId] = useState('');
  const [callerDisplayNumberId, setCallerDisplayNumberId] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const token = await getAccessToken();
        const response = await getPortalBookingState(token);
        if (!cancelled) {
          setState(response);
          if (response.EligibleEvents.length === 1) {
            setEventId(response.EligibleEvents[0].EventId);
          } else if (
            response.EligibleEvents.length === 0 &&
            response.CanBookShared
          ) {
            setEventId(standaloneEventValue);
          }
        }
      } catch (error) {
        if (!cancelled) setMessage(toMessage(error));
      } finally {
        if (!cancelled) setIsBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAccessToken]);

  const selectedEvent = state?.EligibleEvents.find(
    (event) => event.EventId === eventId,
  );
  const isStandalone = Boolean(
    state?.CanBookShared &&
    (
      eventId === standaloneEventValue ||
      state.EligibleEvents.length === 0
    ),
  );

  async function bookPhone(): Promise<void> {
    setIsBusy(true);
    setMessage(null);
    try {
      const token = await getAccessToken();
      await createPortalPhoneBooking(
        token,
        isStandalone ? '' : eventId,
        isStandalone ? callerDisplayNumberId : '',
      );
      void navigate('/my-booking');
    } catch (error) {
      setMessage(toMessage(error));
      setIsBusy(false);
    }
  }

  const canBook = Boolean(
    state &&
    !state.Booking &&
    (
      (isStandalone && callerDisplayNumberId) ||
      (!isStandalone && selectedEvent)
    ),
  );

  const callerDisplayNumberFields = state?.CanBookShared ? (
    <>
      <p>
        Select which outgoing number should be presented for the call.
      </p>
      <label className="booking-field">
        Caller display number
        <select value={callerDisplayNumberId}
          onChange={(event) => setCallerDisplayNumberId(event.target.value)}>
          <option value="">Select a caller display number</option>
          {state.CallingPurposes.map((purpose) => (
            <option key={purpose.CallerDisplayNumberId}
              value={purpose.CallerDisplayNumberId}>
              {purpose.Name} — {purpose.PhoneNumber}
            </option>
          ))}
        </select>
      </label>
      {state.CallingPurposes.length === 0 ? (
        <p className="api-message" role="alert">
          No caller display numbers are currently available. Contact an
          administrator.
        </p>
      ) : null}
    </>
  ) : null;

  return (
    <section className="booking-page">
      <p className="eyebrow">Telephone booking</p>
      <h1>Book a phone</h1>
      <p className="hero-description">
        A random available Phonero telephone will be assigned for up to{' '}
        {state?.MaximumHours ?? 8} hours.
      </p>

      {state?.Booking ? (
        <div className="booking-card">
          <h2>You already have an active booking</h2>
          <p>Release the current telephone before booking another one.</p>
          <Link className="primary-button button-link" to="/my-booking">
            View my booking
          </Link>
        </div>
      ) : null}

      {state && !state.Booking ? (
        <div className="booking-card">
          {state.EligibleEvents.length > 0 ? (
            <>
              <h2>Select event</h2>
              <p>
                Select an active event, or choose a standalone booking. Event
                bookings use only telephones reserved for that event.
              </p>
              <label className="booking-field">
                Event
                <select value={eventId}
                  onChange={(event) => setEventId(event.target.value)}>
                  {state.EligibleEvents.length > 1 ? (
                    <option value="">Select an active event</option>
                  ) : null}
                  {state.EligibleEvents.map((event) => (
                    <option key={event.EventId} value={event.EventId}>
                      {event.Name}
                    </option>
                  ))}
                  {state.CanBookShared ? (
                    <option value={standaloneEventValue}>
                      Book a phone not part of an event
                    </option>
                  ) : null}
                </select>
              </label>

              {isStandalone ? (
                <div className="booking-standalone-options">
                  <h3>Standalone booking</h3>
                  {callerDisplayNumberFields}
                </div>
              ) : null}

              {selectedEvent?.CallerDisplayNumberName ? (
                <p className="booking-selection-note">
                  Caller display number: <strong>
                    {selectedEvent.CallerDisplayNumberName}
                    {selectedEvent.CallerDisplayNumber
                      ? ` — ${selectedEvent.CallerDisplayNumber}`
                      : ''}
                  </strong>
                </p>
              ) : null}
            </>
          ) : state.CanBookShared ? (
            <>
              <h2>Shared telephone pool</h2>
              {callerDisplayNumberFields}
            </>
          ) : (
            <>
              <h2>No active event</h2>
              <p>
                External participants can book a telephone only while they are
                participating in an active event.
              </p>
            </>
          )}

          <button className="primary-button" type="button"
            disabled={isBusy || !canBook}
            onClick={() => void bookPhone()}>
            {isBusy ? 'Booking…' : 'Book a phone'}
          </button>
        </div>
      ) : null}

      {isBusy && !state ? <p>Loading booking options…</p> : null}
      {message ? <p className="api-message" role="alert">{message}</p> : null}
    </section>
  );
}

function toMessage(error: unknown): string {
  if (error instanceof PortalApiError) return error.message;
  return 'The booking request could not be completed.';
}
